import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2, Upload, X, ImageIcon } from "lucide-react";
import { MATERIALS, WORK_LEVELS } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const MAX_IMAGES = 5;
const MAX_DIM = 1600;
const MAX_SIZE_MB = 5;

// Resize image client-side to keep payload reasonable, returns base64 jpeg
const resizeImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, MAX_DIM / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.78));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    material: "",
    work_level: "",
    description: "",
  });
  const [images, setImages] = useState([]); // {dataUrl, name}
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    if (images.length + files.length > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} kép tölthető fel.`);
      return;
    }
    for (const file of files) {
      if (file.size > MAX_SIZE_MB * 1024 * 1024 * 2) {
        toast.error(`A "${file.name}" túl nagy.`);
        continue;
      }
      try {
        const dataUrl = await resizeImage(file);
        setImages((prev) => [...prev, { dataUrl, name: file.name }]);
      } catch {
        toast.error(`Hiba a kép feldolgozásakor: ${file.name}`);
      }
    }
  };

  const removeImage = (idx) =>
    setImages((prev) => prev.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.description) {
      toast.error("Kérem töltse ki a kötelező mezőket.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, images: images.map((i) => i.dataUrl) };
      await axios.post(`${API}/quote`, payload);
      setSuccess(true);
      toast.success("Köszönjük! Hamarosan jelentkezünk.");
      setForm({ name: "", phone: "", email: "", material: "", work_level: "", description: "" });
      setImages([]);
    } catch (err) {
      toast.error("Hiba történt. Kérem próbálja újra, vagy hívjon minket.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="ajanlat"
      data-testid="quote-section"
      className="relative velvet-navy text-[#F5F2EC] py-24 md:py-36 overflow-hidden"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20 bg-[#B8935A]/40" />

      <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 md:gap-16 relative">
        <div className="md:col-span-5">
          <div className="overline mb-6 flex items-center text-[#D4B47A]">
            <span className="inline-block w-7 h-px bg-[#B8935A] mr-3" />
            Árajánlat
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] text-[#F5F2EC]">
            Mondja el,
            <span className="italic text-[#D4B47A]"> mire gondol.</span>
          </h2>
          <p className="mt-7 text-[#F5F2EC]/75 leading-relaxed text-base font-light">
            Csak töltse ki röviden, mit szeretne — küldjön egy-két képet a bútorról, és <strong className="text-[#D4B47A] font-medium">24 órán belül</strong> jelentkezünk személyre szabott ajánlattal. <br/><span className="text-[#F5F2EC]/55 text-sm">Ingyenes, kötelezettségmentes.</span>
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["Gyors válasz", "Ingyenes", "Helyszíni felmérés"].map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5 border border-[#D4B47A]/40 text-[#D4B47A] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em]">
                <span className="w-1 h-1 rounded-full bg-[#D4B47A]" /> {b}
              </span>
            ))}
          </div>
          <div className="mt-12 hidden md:block">
            <p className="overline text-[#D4B47A] mb-3">Vagy hívjon közvetlenül</p>
            <a
              href="tel:+36208241269"
              data-testid="quote-phone-link"
              className="font-serif-display text-4xl text-[#F5F2EC] hover:text-[#D4B47A] transition-colors"
            >
              06 20 824 1269
            </a>
          </div>
        </div>

        <div className="md:col-span-7">
          {success ? (
            <div
              data-testid="quote-success"
              className="border border-[#B8935A]/40 p-12 text-center"
            >
              <CheckCircle2 size={56} className="text-[#D4B47A] mx-auto mb-5" />
              <h3 className="font-serif-display text-4xl mb-4">
                Köszönjük megkeresését!
              </h3>
              <p className="text-[#F5F2EC]/70 font-light">
                A megadott elérhetőségen 24 órán belül jelentkezünk.
              </p>
              <button
                onClick={() => setSuccess(false)}
                data-testid="quote-reset-btn"
                className="mt-10 text-xs uppercase tracking-[0.3em] text-[#D4B47A] hover:text-[#F5F2EC] transition-colors border-b border-[#D4B47A]/50 pb-1"
              >
                Új megkeresés küldése
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-8">
              <div className="grid md:grid-cols-2 gap-7">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D4B47A] mb-3">
                    Név *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    data-testid="quote-input-name"
                    className="w-full bg-transparent border-b border-[#F5F2EC]/25 focus:border-[#D4B47A] outline-none py-3 text-[#F5F2EC] placeholder-[#F5F2EC]/30"
                    placeholder="Az Ön neve"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D4B47A] mb-3">
                    Telefonszám *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="quote-input-phone"
                    className="w-full bg-transparent border-b border-[#F5F2EC]/25 focus:border-[#D4B47A] outline-none py-3 text-[#F5F2EC] placeholder-[#F5F2EC]/30"
                    placeholder="06 ..."
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D4B47A] mb-3">
                  E-mail
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  data-testid="quote-input-email"
                  className="w-full bg-transparent border-b border-[#F5F2EC]/25 focus:border-[#D4B47A] outline-none py-3 text-[#F5F2EC] placeholder-[#F5F2EC]/30"
                  placeholder="nev@email.hu"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-7">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D4B47A] mb-3">
                    Anyag típusa
                  </label>
                  <select
                    name="material"
                    value={form.material}
                    onChange={onChange}
                    data-testid="quote-input-material"
                    className="select-dark w-full bg-transparent border-b border-[#F5F2EC]/25 focus:border-[#D4B47A] outline-none py-3 text-[#F5F2EC] pr-6"
                  >
                    <option value="" className="bg-[#0A1228]">
                      Válasszon...
                    </option>
                    {MATERIALS.map((m) => (
                      <option key={m} value={m} className="bg-[#0A1228]">
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D4B47A] mb-3">
                    Munka szintje
                  </label>
                  <select
                    name="work_level"
                    value={form.work_level}
                    onChange={onChange}
                    data-testid="quote-input-work-level"
                    className="select-dark w-full bg-transparent border-b border-[#F5F2EC]/25 focus:border-[#D4B47A] outline-none py-3 text-[#F5F2EC] pr-6"
                  >
                    <option value="" className="bg-[#0A1228]">
                      Válasszon...
                    </option>
                    {WORK_LEVELS.map((w) => (
                      <option key={w} value={w} className="bg-[#0A1228]">
                        {w}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D4B47A] mb-3">
                  Elképzelés / leírás *
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={onChange}
                  data-testid="quote-input-description"
                  rows={4}
                  className="w-full bg-transparent border-b border-[#F5F2EC]/25 focus:border-[#D4B47A] outline-none py-3 text-[#F5F2EC] placeholder-[#F5F2EC]/30 resize-none"
                  placeholder="Pl. egy 3 személyes kanapét szeretnék újrakárpitozni, sötét bordó színű szövettel..."
                  required
                />
              </div>

              {/* Image upload */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D4B47A] mb-3">
                  Képfeltöltés ({images.length}/{MAX_IMAGES})
                </label>
                <div
                  className={`dropzone ${dragging ? "dragover" : ""} p-6 text-center cursor-pointer`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    handleFiles(e.dataTransfer.files);
                  }}
                  data-testid="quote-dropzone"
                >
                  <Upload size={24} className="mx-auto text-[#D4B47A] mb-2" />
                  <p className="text-sm text-[#F5F2EC]/80">
                    Kattintson, vagy húzza ide a képeket
                  </p>
                  <p className="text-xs text-[#F5F2EC]/50 mt-1 font-light">
                    JPG, PNG · max {MAX_IMAGES} db · automatikusan optimalizálva
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                    data-testid="quote-file-input"
                  />
                </div>

                {images.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
                    {images.map((img, idx) => (
                      <div
                        key={idx}
                        className="relative aspect-square group"
                        data-testid={`quote-image-preview-${idx}`}
                      >
                        <img
                          src={img.dataUrl}
                          alt={img.name}
                          className="w-full h-full object-cover border border-[#F5F2EC]/15"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          data-testid={`quote-image-remove-${idx}`}
                          className="absolute -top-2 -right-2 bg-[#0A1228] border border-[#D4B47A]/60 text-[#D4B47A] hover:bg-[#1E47A8] hover:text-white rounded-full p-1 transition-colors"
                          aria-label="Kép eltávolítása"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit-btn"
                className="group inline-flex items-center gap-3 bg-[#1E47A8] hover:bg-[#2E5BC4] disabled:opacity-60 text-white px-10 py-4 text-xs uppercase tracking-[0.3em] transition-colors mt-4"
              >
                {loading ? "Küldés..." : "Kérek ajánlatot"}
                <Send
                  size={16}
                  className="transition-transform group-hover:translate-x-1.5"
                />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
